// src/pages/hr/HrPoliciesPage.jsx
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Box, Button, Card, CardContent, Chip, Divider, FormControl, InputLabel,
  MenuItem, Select, Stack, TextField, Typography, IconButton, Tooltip, List,
  ListItemButton, ListItemText, Dialog, DialogTitle, DialogContent, DialogActions
} from "@mui/material";
import {
  Add as AddIcon, Edit as EditIcon, Save as SaveIcon, Close as CloseIcon,
  FormatBold as BoldIcon, FormatItalic as ItalicIcon, FormatUnderlined as UnderlineIcon,
  FormatListBulleted as BulletedIcon, FormatListNumbered as NumberedIcon,
  FormatQuote as QuoteIcon, Title as TitleIcon, AlignHorizontalLeft as AlignLeftIcon,
  AlignHorizontalCenter as AlignCenterIcon, AlignHorizontalRight as AlignRightIcon,
  CleaningServices as ClearIcon, ColorLens as ColorIcon, Highlight as HighlighterIcon,
  ContentCut as PageBreakIcon, Search as SearchIcon
} from "@mui/icons-material";

import {
  collection, addDoc, onSnapshot, orderBy, query, serverTimestamp, doc, updateDoc
} from "firebase/firestore";
import { firestore } from "../../firebase";

/* ----- Tiptap ----- */
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { Underline } from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import TextAlign from "@tiptap/extension-text-align";
import { TextStyle } from "@tiptap/extension-text-style";
import { Color } from "@tiptap/extension-color";
import { Highlight } from "@tiptap/extension-highlight";
import Heading from "@tiptap/extension-heading";

/* ---------------- helpers ---------------- */
const STATUS_OPTIONS = [
  { value: "ONGOING", label: "Ongoing" },
  { value: "UPCOMING", label: "Upcoming" },
  { value: "DRAFT", label: "Draft" },
];

const parseVersion = (v = "") => {
  const m = String(v).trim().match(/^(\d+)\.(\d+)\.(\d+)$/);
  if (!m) return [1, 0, 0];
  return [Number(m[1]), Number(m[2]), Number(m[3])];
};
const cmpVersion = (a, b) => {
  const A = parseVersion(a), B = parseVersion(b);
  if (A[0] !== B[0]) return A[0] - B[0];
  if (A[1] !== B[1]) return A[1] - B[1];
  return A[2] - B[2];
};
const bumpPatch = (v = "1.0.0") => {
  const [x, y, z] = parseVersion(v);
  return `${x}.${y}.${z + 1}`;
};
const todayISO = () => new Date().toISOString().slice(0, 10);

const deriveComputedStatus = (rawStatus, effectiveFrom) => {
  const r = (rawStatus || "DRAFT").toUpperCase();
  if (r === "DRAFT") return "DRAFT";
  if (!effectiveFrom) return r === "UPCOMING" ? "UPCOMING" : "ONGOING";
  return effectiveFrom <= todayISO() ? "ONGOING" : "UPCOMING";
};

/* small toolbar button */
function Tb({ title, active, onClick, children }) {
  return (
    <Tooltip title={title}>
      <IconButton
        size="small"
        onClick={onClick}
        sx={{ borderRadius: 1, bgcolor: active ? "action.selected" : "transparent" }}
      >
        {children}
      </IconButton>
    </Tooltip>
  );
}

export default function HrPoliciesPage() {
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);

  // dialog & form
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("");
  const [effectiveFrom, setEffectiveFrom] = useState("");
  const [status, setStatus] = useState("UPCOMING");
  const [version, setVersion] = useState("1.0.0");
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");

  // tiptap content
  const [content, setContent] = useState("<p></p>");
  const pendingSet = useRef(null);

  /* ----- editor ----- */
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        bulletList: { keepMarks: true, keepAttributes: true },
        orderedList: { keepMarks: true, keepAttributes: true },
        blockquote: true,
      }),
      Underline,
      Link.configure({ openOnClick: true, autolink: true, defaultProtocol: "https" }),
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      TextStyle,
      Color,
      Highlight.configure({ multicolor: true }),
      Heading.configure({ levels: [1, 2, 3, 4] }),
    ],
    content,
    onUpdate: ({ editor }) => setContent(editor.getHTML()),
  });

  useEffect(() => {
    if (!editor) return;
    if (pendingSet.current !== null) {
      editor.commands.setContent(pendingSet.current);
      pendingSet.current = null;
    }
  }, [editor, dialogOpen]);

  /* load policies */
  useEffect(() => {
    const q = query(collection(firestore, "hrPolicies"), orderBy("updatedAt", "desc"));
    const unsub = onSnapshot(
      q,
      (snap) => {
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setPolicies(rows);
        setLoading(false);
      },
      (err) => {
        console.error("hrPolicies onSnapshot error", err);
        setLoading(false);
      }
    );
    return () => unsub();
  }, []);

  /* auto bump version for NEW policy when title changes */
  useEffect(() => {
    if (editingId) return;
    const sameTitle = policies.filter(
      (p) => (p.title || "").trim().toLowerCase() === title.trim().toLowerCase()
    );
    const pool = sameTitle.length ? sameTitle : policies;
    const latest = pool.reduce(
      (acc, p) => (cmpVersion(p.version || "0.0.0", acc) > 0 ? (p.version || "0.0.0") : acc),
      "0.0.0"
    );
    setVersion(latest === "0.0.0" ? "1.0.0" : bumpPatch(latest));
  }, [title, policies, editingId]);

  /* grouping for list panels */
  const grouped = useMemo(() => {
    const b = { ONGOING: [], UPCOMING: [], OTHER: [] };
    const term = search.trim().toLowerCase();
    policies.forEach((p) => {
      if (term && !(p.title || "").toLowerCase().includes(term)) return;
      const c = deriveComputedStatus(p.status, p.effectiveFrom);
      const row = { ...p, computedStatus: c };
      if (c === "ONGOING") b.ONGOING.push(row);
      else if (c === "UPCOMING") b.UPCOMING.push(row);
      else b.OTHER.push(row);
    });
    return b;
  }, [policies, search]);

  /* actions */
  const resetForm = () => {
    setEditingId(null);
    setTitle("");
    setCategory("");
    setEffectiveFrom("");
    setStatus("UPCOMING");
    setVersion("1.0.0");
    setContent("<p></p>");
    pendingSet.current = "<p></p>";
    if (editor) editor.commands.setContent("<p></p>");
  };

  const openNew = () => {
    resetForm();
    setDialogOpen(true);
  };

  const startEdit = (p) => {
    setEditingId(p.id);
    setTitle(p.title || "");
    setCategory(p.category || "");
    setEffectiveFrom(p.effectiveFrom || "");
    setStatus(p.status || "DRAFT");
    setVersion(p.version || "1.0.0");
    const html = p.body || "<p></p>";
    setContent(html);
    pendingSet.current = html;
    if (editor) editor.commands.setContent(html);
    setDialogOpen(true);
  };

  const normalizeStatusOnSave = (pickedStatus, eff) => {
    if ((pickedStatus || "DRAFT") === "DRAFT") return "DRAFT";
    if (!eff) return pickedStatus || "ONGOING";
    return eff <= todayISO() ? "ONGOING" : "UPCOMING";
  };

  const handleSave = async () => {
    if (!title.trim()) return alert("Please enter a policy title.");
    setSaving(true);
    const html = editor ? editor.getHTML() : content;

    const nextStatus = normalizeStatusOnSave(status, effectiveFrom);

    const payload = {
      title: title.trim(),
      category: category.trim() || "General",
      status: nextStatus,     // normalized DRAFT/UPCOMING/ONGOING
      version: version || "1.0.0",
      body: html || "<p></p>",
      effectiveFrom: effectiveFrom || "",
      updatedAt: serverTimestamp(),
    };

    try {
      if (editingId) {
        await updateDoc(doc(firestore, "hrPolicies", editingId), payload);
      } else {
        await addDoc(collection(firestore, "hrPolicies"), {
          ...payload,
          createdAt: serverTimestamp(),
        });
      }
      setSaving(false);
      setDialogOpen(false);
      resetForm();
    } catch (err) {
      console.error("Save failed", err);
      alert("Save failed: " + err.message);
      setSaving(false);
    }
  };

  const PolicyList = ({ items, emptyText }) => (
    <List dense disablePadding>
      {items.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
          {emptyText}
        </Typography>
      ) : (
        items.map((p) => (
          <ListItemButton key={p.id} onClick={() => startEdit(p)} sx={{ borderRadius: 1.5, mb: 0.5 }}>
            <ListItemText
              primary={
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                    {p.title || "Untitled policy"}
                  </Typography>
                  <Chip size="small" label={p.version || "1.0.0"} variant="outlined" sx={{ height: 18, fontSize: 10 }} />
                  <Chip
                    size="small"
                    label={p.computedStatus || p.status}
                    color={
                      (p.computedStatus || p.status) === "ONGOING"
                        ? "success"
                        : (p.computedStatus || p.status) === "UPCOMING"
                        ? "warning"
                        : "default"
                    }
                    sx={{ height: 18, fontSize: 10 }}
                  />
                </Stack>
              }
              secondary={
                <Typography variant="caption" color="text.secondary" noWrap>
                  {(p.category || "General") + (p.effectiveFrom ? ` • Effective ${p.effectiveFrom}` : "")}
                </Typography>
              }
            />
            <Tooltip title="Edit">
              <IconButton size="small" edge="end">
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </ListItemButton>
        ))
      )}
    </List>
  );

  /* ----- rich toolbar ----- */
  const Toolbar = () => {
    if (!editor) return null;
    const isActive = (name, attrs = {}) => editor.isActive(name, attrs);

    // Insert <!--pagebreak--> at current selection
    const insertPageBreak = () => {
      const html = editor.getHTML();
      // insert a break at caret by using a unique marker + replaceSelection
      editor.chain().focus().insertContent('<!--pagebreak-->').run();
      const withMarker = editor.getHTML();
      const updated = withMarker.replace('<!--pagebreak-->', '<!--pagebreak-->');
      editor.commands.setContent(updated, false); // keep history
    };

    return (
      <Stack
        direction="row"
        spacing={0.5}
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderTopLeftRadius: 1,
          borderTopRightRadius: 1,
          px: 0.5,
          py: 0.5,
          flexWrap: "wrap",
          alignItems: "center",
          mb: 0.5,
        }}
      >
        {/* Headings */}
        <Tb title="Heading 1" active={isActive("heading", { level: 1 })}
            onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}>
          <TitleIcon fontSize="small" /><Typography variant="caption" sx={{ ml: 0.5 }}>H1</Typography>
        </Tb>
        <Tb title="Heading 2" active={isActive("heading", { level: 2 })}
            onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}>
          <TitleIcon fontSize="small" /><Typography variant="caption" sx={{ ml: 0.5 }}>H2</Typography>
        </Tb>
        <Tb title="Heading 3" active={isActive("heading", { level: 3 })}
            onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}>
          <TitleIcon fontSize="small" /><Typography variant="caption" sx={{ ml: 0.5 }}>H3</Typography>
        </Tb>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        {/* Marks */}
        <Tb title="Bold" active={isActive("bold")} onClick={() => editor.chain().focus().toggleBold().run()}>
          <BoldIcon fontSize="small" />
        </Tb>
        <Tb title="Italic" active={isActive("italic")} onClick={() => editor.chain().focus().toggleItalic().run()}>
          <ItalicIcon fontSize="small" />
        </Tb>
        <Tb title="Underline" active={isActive("underline")} onClick={() => editor.chain().focus().toggleUnderline().run()}>
          <UnderlineIcon fontSize="small" />
        </Tb>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        {/* Lists / Quote */}
        <Tb title="Bulleted list" active={isActive("bulletList")} onClick={() => editor.chain().focus().toggleBulletList().run()}>
          <BulletedIcon fontSize="small" />
        </Tb>
        <Tb title="Numbered list" active={isActive("orderedList")} onClick={() => editor.chain().focus().toggleOrderedList().run()}>
          <NumberedIcon fontSize="small" />
        </Tb>
        <Tb title="Blockquote" active={isActive("blockquote")} onClick={() => editor.chain().focus().toggleBlockquote().run()}>
          <QuoteIcon fontSize="small" />
        </Tb>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        {/* Align */}
        <Tb title="Align left" active={isActive({ textAlign: "left" })}
            onClick={() => editor.chain().focus().setTextAlign("left").run()}>
          <AlignLeftIcon fontSize="small" />
        </Tb>
        <Tb title="Align center" active={isActive({ textAlign: "center" })}
            onClick={() => editor.chain().focus().setTextAlign("center").run()}>
          <AlignCenterIcon fontSize="small" />
        </Tb>
        <Tb title="Align right" active={isActive({ textAlign: "right" })}
            onClick={() => editor.chain().focus().setTextAlign("right").run()}>
          <AlignRightIcon fontSize="small" />
        </Tb>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        {/* Color & Highlight */}
        <Tb title="Text color (indigo)" onClick={() => editor.chain().focus().setColor("#2b6cb0").run()}>
          <ColorIcon fontSize="small" />
        </Tb>
        <Tb title="Clear color" onClick={() => editor.chain().focus().unsetColor().run()}>
          <ClearIcon fontSize="small" />
        </Tb>
        <Tb title="Yellow highlight" active={isActive("highlight")}
            onClick={() => editor.chain().focus().setHighlight({ color: "#fff59d" }).run()}>
          <HighlighterIcon fontSize="small" />
        </Tb>
        <Tb title="Clear highlight" onClick={() => editor.chain().focus().unsetHighlight().run()}>
          <ClearIcon fontSize="small" />
        </Tb>

        {/* Font Size Selector */}
        <FormControl size="small" sx={{ minWidth: 120, ml: 1 }}>
          <Select
            value=""
            displayEmpty
            renderValue={() => "Font Size"}
            onChange={(e) => {
              const size = e.target.value;
              if (!size) return;
              editor.chain().focus().setMark("textStyle", { fontSize: size }).run();
            }}
          >
            {["12px", "14px", "16px", "18px", "20px", "24px", "28px", "32px"].map((fs) => (
              <MenuItem key={fs} value={fs}>
                {fs}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        {/* Page break */}
        <Tb title="Insert page break" onClick={insertPageBreak}>
          <PageBreakIcon fontSize="small" />
        </Tb>

        <Tb title="Clear formatting" onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()}>
          <ClearIcon fontSize="small" />
        </Tb>
      </Stack>
    );
  };

  const CountChip = ({ label, count, color = "default" }) => (
    <Chip
      size="small"
      label={`${label} (${count})`}
      color={color}
      variant={color === "default" ? "outlined" : "filled"}
      sx={{ height: 22, fontSize: 11 }}
    />
  );

  return (
    <Box>
      {/* Header */}
      <Stack
        direction={{ xs: "column", md: "row" }}
        alignItems={{ xs: "flex-start", md: "center" }}
        justifyContent="space-between"
        spacing={1.5}
        mb={2.5}
      >
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
            HR Policies
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Versioned policies with <b>Ongoing</b>/<b>Upcoming</b>. Employees only see the latest
            <b> ONGOING</b> and the nearest <b>UPCOMING</b>. On the effective date,<><br /></> UPCOMING becomes ONGOING.
          </Typography>
        </Box>
        <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={openNew}>
          New Policy
        </Button>
      </Stack>

      <Stack direction={{ xs: "column", md: "row" }} spacing={2.5}>
        {/* Left: lists */}
        <Card
          sx={{
            flexBasis: { xs: "100%", md: 380 },
            flexShrink: 0,
            maxHeight: "72vh",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <CardContent sx={{ pb: 1, display: "flex", flexDirection: "column", height: "100%", gap: 1.25 }}>
            <TextField
              size="small"
              placeholder="Search by title…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{ startAdornment: <SearchIcon fontSize="small" sx={{ mr: 0.5, opacity: 0.6 }} /> }}
            />

            <Stack direction="row" spacing={1} alignItems="center">
              <CountChip label="Ongoing" count={grouped.ONGOING.length} color="success" />
              <CountChip label="Upcoming" count={grouped.UPCOMING.length} color="warning" />
              <CountChip label="Draft/Other" count={grouped.OTHER.length} />
            </Stack>

            <Divider />

            <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 0.5 }}>Ongoing</Typography>
            <PolicyList items={grouped.ONGOING} emptyText="No ongoing policies." />

            <Divider sx={{ my: 1 }} />

            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Upcoming</Typography>
            <PolicyList items={grouped.UPCOMING} emptyText="No upcoming policies." />

            <Divider sx={{ my: 1 }} />

            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Drafts & Others</Typography>
            <PolicyList items={grouped.OTHER} emptyText="No drafts yet." />
          </CardContent>
        </Card>

        {/* Right: guidance (kept light for now) */}
        <Card sx={{ flex: 1, minHeight: 360 }}>
          <CardContent sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Authoring tips</Typography>
            <Typography variant="body2" color="text.secondary">
              Use <b>Insert page break</b> to split very long policies. The employee reader will render
              each page as a separate section for easier reading.
            </Typography>
          </CardContent>
        </Card>
      </Stack>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="lg">
        <DialogTitle sx={{ pb: 1 }}>{editingId ? "Edit Policy" : "Create New Policy"}</DialogTitle>
        <DialogContent dividers>
          <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mb: 2 }}>
            <TextField
              label="Policy title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              fullWidth
              size="small"
            />
            <TextField
              label="Category"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              size="small"
              sx={{ minWidth: 160 }}
            />
          </Stack>

          <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mb: 2 }}>
            <TextField label="Version" size="small" value={version} InputProps={{ readOnly: true }} sx={{ minWidth: 140 }} />
            <TextField
              label="Effective from"
              type="date"
              size="small"
              value={effectiveFrom}
              onChange={(e) => setEffectiveFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 200 }}
            />
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Status</InputLabel>
              <Select label="Status" value={status} onChange={(e) => setStatus(e.target.value)}>
                {STATUS_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>

          {/* Editor */}
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
            Policy content
          </Typography>

          <Toolbar />

          <Box
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderBottomLeftRadius: 1,
              borderBottomRightRadius: 1,
              minHeight: 260,
              p: 1.5,
              mt: -0.5,
              "& .tiptap": {
                fontSize: 14,
                lineHeight: 1.6,
                outline: "none",
                minHeight: 240,
              },
              "& .tiptap h1": { fontSize: "1.6rem", margin: "0.5rem 0" },
              "& .tiptap h2": { fontSize: "1.3rem", margin: "0.5rem 0" },
              "& .tiptap h3": { fontSize: "1.15rem", margin: "0.5rem 0" },
              "& .tiptap blockquote": {
                borderLeft: "3px solid #ddd",
                margin: "8px 0",
                padding: "4px 10px",
                color: "text.secondary",
              },
              "& .tiptap ul, & .tiptap ol": { paddingLeft: "1.25rem" },
            }}
          >
            <EditorContent editor={editor} className="tiptap" />
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button
            variant="text"
            startIcon={<CloseIcon />}
            onClick={() => {
              setDialogOpen(false);
              resetForm();
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={saving || !title.trim()}
          >
            {saving ? "Saving..." : "Save Policy"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
